import React from 'react';
import { useTranslation } from 'react-i18next';
import { i18prefix } from '../helpers';
import { SelectDropdown } from '../../../../../components/Select/SelectDropdown';
import { ProjectViewType, Timesheet } from '../types';
import WeekNav from '../../../../../components/WeekNav';
import { startOfISOWeek, startOfMonth } from 'date-fns';
import {
  toISODateOnly,
  isSameOrBefore,
  isSameOrAfter,
} from '../../../../../utils/dateUtils';
import MonthNav from '../../../../../components/MonthNav';
import {
  WorkloadType,
  WorkloadTrackingType,
} from '../../../__generated__/graphql';
import { SwitchInput } from '../../../../../components/form';
import { useMyTrackingTypes } from '../../../hooks/timeTracking/useMyTrackingTypes';
import { useMyTimeTrackingSettings } from '../../../hooks/timeTracking/useMyTimeTrackingSettings';
import Hint from '../../../../../components/hint';

type Props = {
  timesheets: Timesheet[];
  selectedViewType: ProjectViewType;
  selectedTimesheet: Timesheet;
  showTrackedTime: boolean;
  onTimesheetChange: (startDate: string) => void;
  onViewTypeChange: (viewType: ProjectViewType) => void;
  onShowTrackedTimeChange: (showTrackedTime: boolean) => void;
};

const SelectionPanel: React.FC<React.PropsWithChildren<Props>> = ({
  timesheets,
  selectedTimesheet,
  selectedViewType,
  showTrackedTime,
  onViewTypeChange,
  onTimesheetChange,
  onShowTrackedTimeChange,
}) => {
  const { t } = useTranslation();

  const { isTimeTrackingEnabled } = useMyTrackingTypes();
  const { data: trackingSettingsData } = useMyTimeTrackingSettings();
  const workloadTrackingType =
    trackingSettingsData?.me.workloads[0]?.flair__Time_Tracking_Type__c;
  const isTimeTrackingWithProjects =
    workloadTrackingType === WorkloadTrackingType.TimeTrackingWithProjects;

  return (
    <>
      {isTimeTrackingEnabled && (
        <div className="ms-auto d-flex align-items-center gap-2 me-3">
          <span>{t(`${i18prefix}.topPanel.showTrackedTime`)}</span>
          {isTimeTrackingWithProjects ? (
            <Hint
              id="showTrackedTimeHint"
              mode="tooltip"
              text={t(`${i18prefix}.topPanel.showTrackedTimeAlwaysEnabled`)}
              placement="top">
              <SwitchInput
                name="showTrackedTime"
                checked={true}
                onChange={onShowTrackedTimeChange}
                disabled={true}
              />
            </Hint>
          ) : (
            <SwitchInput
              name="showTrackedTime"
              checked={showTrackedTime}
              onChange={onShowTrackedTimeChange}
              disabled={false}
            />
          )}
        </div>
      )}
      <div
        className={`${
          !isTimeTrackingEnabled ? 'ms-auto ' : ''
        }me-2 text-muted`}>
        {t(`${i18prefix}.topPanel.viewBy`)}
      </div>
      <SelectDropdown
        className="pe-2"
        options={[
          {
            value: 'BY_PROJECTS',
            label: t(`${i18prefix}.topPanel.projects`),
          },
          { value: 'BY_DAYS', label: t(`${i18prefix}.topPanel.days`) },
        ]}
        value={selectedViewType}
        onChange={(val: string | null) => {
          isViewType(val) && onViewTypeChange(val);
        }}
      />
      <Nav
        timesheets={timesheets}
        selectedTimesheet={selectedTimesheet}
        onTimesheetChange={onTimesheetChange}></Nav>
    </>
  );
};

const Nav: React.FC<
  React.PropsWithChildren<
    Pick<Props, 'timesheets' | 'onTimesheetChange' | 'selectedTimesheet'>
  >
> = ({ timesheets, selectedTimesheet, onTimesheetChange }) => {
  const minDate = timesheets[0]?.startDate;
  const maxDate = timesheets[timesheets.length - 1]?.startDate;

  const handleNavChange = (newValue: Date) => {
    const matchingTimesheet = timesheets.find((timesheet) => {
      return (
        isSameOrBefore(timesheet.startDate, newValue, { unit: 'day' }) &&
        isSameOrAfter(timesheet.endDate, newValue, { unit: 'day' })
      );
    });

    if (matchingTimesheet) {
      onTimesheetChange(toISODateOnly(newValue));
    }
  };

  switch (selectedTimesheet.timesheetType) {
    case WorkloadType.Monthly:
      return (
        <MonthNav
          minMonth={startOfMonth(minDate)}
          maxMonth={startOfMonth(maxDate)}
          currentMonth={startOfMonth(selectedTimesheet.startDate)}
          onMonthChanged={handleNavChange}
        />
      );

    default:
      return (
        <WeekNav
          minWeek={startOfISOWeek(minDate)}
          maxWeek={startOfISOWeek(maxDate)}
          currentWeek={startOfISOWeek(selectedTimesheet.startDate)}
          onWeekChanged={handleNavChange}
        />
      );
  }
};

export default SelectionPanel;

function isViewType(str: string | null): str is ProjectViewType {
  return str === 'BY_DAYS' || str === 'BY_PROJECTS';
}
